import { z } from "zod";

import { todayDateOnly } from "@/lib/dateOnly";

import type {
  DuplicateMatch,
  DuplicateQuery,
  PassportScan
} from "../../../api/types/clientDirectory";
import type {
  CreateForeignNationalRequest,
  UpdateForeignNationalRequest
} from "../../../api/types/foreignNational";
import type { PassportPayload } from "../../../api/types/foreignNationalRecords";
import { matchCountry } from "../../../components/form/countries";

const YMD = /^\d{4}-\d{2}-\d{2}$/;

export const newClientSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  middleName: z.string(),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: z
    .string()
    .trim()
    .refine(
      (v) => !v || z.string().email().safeParse(v).success,
      "Enter a valid email"
    ),
  birthday: z
    .string()
    .refine((v) => !v || YMD.test(v), "Use a full date")
    .refine(
      (v) => !v || v <= todayDateOnly(),
      "Date of birth can't be in the future"
    ),
  phone: z.string(),
  preferredLanguage: z.string(),
  citizenship: z.string(),
  assignedUserIds: z.array(z.string())
});

export type NewClientValues = z.infer<typeof newClientSchema>;

export type OptionalField =
  | "middleName"
  | "birthday"
  | "phone"
  | "preferredLanguage"
  | "citizenship";

export const OPTIONAL_FIELDS: ReadonlyArray<{
  key: OptionalField;
  label: string;
}> = [
  { key: "middleName", label: "Middle Name" },
  { key: "birthday", label: "Date of Birth" },
  { key: "phone", label: "Phone Number" },
  { key: "preferredLanguage", label: "Preferred Language" },
  { key: "citizenship", label: "Country of Citizenship" }
];

export const emptyNewClient = (currentUserId?: string): NewClientValues => ({
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  birthday: "",
  phone: "",
  preferredLanguage: "",
  citizenship: "",
  assignedUserIds: currentUserId ? [currentUserId] : []
});

const clean = (v: string) => v.trim() || undefined;

export const toCreatePayload = (
  values: NewClientValues
): CreateForeignNationalRequest => ({
  first_name: values.firstName.trim(),
  middle_name: clean(values.middleName),
  last_name: values.lastName.trim(),
  email: clean(values.email),
  birthday: values.birthday ? `${values.birthday}T00:00:00.000Z` : undefined,
  phone: clean(values.phone),
  assigned_to_user_ids:
    values.assignedUserIds.length > 0 ? values.assignedUserIds : undefined
});

/**
 * Profile fields the create endpoint doesn't take. Values that came from the
 * passport scan carry it as their source; anything typed is manual.
 */
export const profileUpdates = (
  values: NewClientValues,
  scan: PassportScan | null
): UpdateForeignNationalRequest[] => {
  const fromScan: UpdateForeignNationalRequest = {};
  const typed: UpdateForeignNationalRequest = {};
  const citizenship = values.citizenship.trim();
  if (citizenship) {
    const scanned =
      scan && matchCountry(scan.citizenship) === citizenship ? fromScan : typed;
    scanned.citizenships = [citizenship];
  }
  if (scan?.city_of_birth) fromScan.city_of_birth = scan.city_of_birth;
  const country = matchCountry(scan?.country_of_birth);
  if (country) fromScan.country_of_birth = country;
  if (values.preferredLanguage) {
    typed.preferred_language = values.preferredLanguage;
  }
  const out: UpdateForeignNationalRequest[] = [];
  if (Object.keys(fromScan).length > 0) {
    out.push({ ...fromScan, source: { kind: "document", label: "Passport" } });
  }
  if (Object.keys(typed).length > 0) out.push(typed);
  return out;
};

export const passportFromScan = (
  scan: PassportScan | null
): PassportPayload | null => {
  const p = scan?.passport;
  if (!p || !(p.passport_number || p.expiration_date)) return null;
  return {
    passport_number: p.passport_number,
    issuing_country: matchCountry(p.issuing_country) ?? p.issuing_country,
    issue_date: p.issue_date,
    expiration_date: p.expiration_date,
    is_current: true,
    source: { kind: "document", label: "Passport" }
  };
};

/**
 * Form values to fill from a scan, and the optional fields that now have a
 * value and should be shown.
 */
export const applyScan = (
  scan: PassportScan
): {
  values: Partial<NewClientValues>;
  reveal: OptionalField[];
} => {
  const values: Partial<NewClientValues> = {};
  const reveal: OptionalField[] = [];
  if (scan.first_name) values.firstName = scan.first_name;
  if (scan.last_name) values.lastName = scan.last_name;
  if (scan.middle_name) {
    values.middleName = scan.middle_name;
    reveal.push("middleName");
  }
  if (scan.birthday) {
    values.birthday = scan.birthday;
    reveal.push("birthday");
  }
  const citizenship = matchCountry(scan.citizenship);
  if (citizenship) {
    values.citizenship = citizenship;
    reveal.push("citizenship");
  }
  return { values, reveal };
};

/** What to ask the duplicate check, or null until there's enough to match on. */
export const duplicateQuery = (
  values: Pick<NewClientValues, "firstName" | "lastName" | "email" | "birthday">
): DuplicateQuery | null => {
  const first = values.firstName.trim();
  const last = values.lastName.trim();
  const email = values.email.trim();
  const hasName = first.length >= 2 && last.length >= 2;
  const hasEmail = z.string().email().safeParse(email).success;
  if (!hasName && !hasEmail) return null;
  const query: DuplicateQuery = {};
  if (hasName) {
    query.first_name = first;
    query.last_name = last;
  }
  if (hasEmail) query.email = email;
  if (YMD.test(values.birthday)) query.birthday = values.birthday;
  return query;
};

/** Strong matches first; a name-only match is shown only with nothing stronger. */
export const rankDuplicates = (
  matches: DuplicateMatch[],
  dismissed: ReadonlySet<string>
): DuplicateMatch[] => {
  const open = matches.filter((m) => !dismissed.has(m.foreign_national_id));
  const strong = open.filter((m) => m.strong);
  return strong.length > 0 ? strong : open;
};

const formatBorn = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
};

export const duplicateSummary = (match: DuplicateMatch): string =>
  [
    match.reason,
    match.email,
    match.birthday ? `Born ${formatBorn(match.birthday)}` : null,
    match.archived ? "Archived" : null
  ]
    .filter(Boolean)
    .join(" · ");
